import { useQuery } from '@tanstack/react-query';
import { realtimeDatabase } from '@util/firebase';
import { getUserById } from '@util/firestore/users';
import { useAuth } from 'context/AuthContext';
import { onValue, ref } from 'firebase/database';
import { memo, useEffect, useState } from 'react';
import { cn, formatTimestamp, timeAgo } from '..';

export interface OnlineStatus {
  state: 'online' | 'offline';
  last_changed: number;
  web?: number;
  app?: number;
  web_info?: {
    ua: string;
  };
  app_info?: {
    platform: string;
    version: number;
  };
  // legacy versions
  platform?: {
    web?: number;
    app?: number;
  };
}

export const useOnlineStatus = (uid?: string) => {
  const [status, setStatus] = useState<OnlineStatus | null>(null);

  useEffect(() => {
    if (!uid) return;

    const statusRef = ref(realtimeDatabase, `/status/${uid}`);

    return onValue(statusRef, (snapshot) => {
      const next = snapshot.val();

      setStatus((prev) => {
        // prevent re-render if nothing meaningful changed
        if (
          prev?.state === next?.state &&
          prev?.last_changed === next?.last_changed
        ) {
          return prev;
        }
        return next;
      });
    });
  }, [uid]);

  return { status };
};

export const StatusLabel = memo(function StatusLabel({
  status,
  hide_status,
  showLastLogin,
  recipientDocUid,
  className,
}: {
  status: OnlineStatus | null;
  hide_status?: boolean;
  showLastLogin?: boolean;
  recipientDocUid?: string;
  className?: string;
}) {
  const { userDoc: currUser } = useAuth();
  const { data: recipientDoc } = useQuery({
    queryKey: ['user', recipientDocUid],
    queryFn: () => getUserById(recipientDocUid!),
    enabled: !!currUser?.roles?.support && !!recipientDocUid,
  });
  return !!status?.last_changed &&
    (!hide_status || currUser?.roles?.support) ? (
    <div className="flex items-center gap-[0.6rem]">
      {status?.state === 'online' ? (
        <>
          <i
            // Tailwind not applying heights to these so I used inline styles...
            style={{
              height: '0.8rem',
              width: '0.8rem',
            }}
            className="rounded-full bg-green-500"
          ></i>
          <span className={cn('text-[1.6rem] font-normal', className)}>
            Online
            {currUser?.roles?.support && (
              <span className={cn('text-[1.3rem] font-normal', className)}>
                {` (${(status.web ?? 0) > (status.app ?? 0) ? 'Web' : 'App'})`}
              </span>
            )}
          </span>
        </>
      ) : (
        <div className="flex flex-col">
          <div className="flex items-center gap-[0.4rem]">
            <div
              // Tailwind not applying heights to these so I used inline styles...
              style={{
                height: '0.8rem',
                width: '0.8rem',
              }}
              className=" rounded-full bg-brand-gray"
            ></div>
            <span className={cn('text-[1.6rem] font-normal', className)}>
              Last seen {timeAgo(status?.last_changed)}{' '}
              {currUser?.roles?.support && (
                <span className={cn('text-[1.3rem] font-normal', className)}>
                  {` (${
                    (status.web ?? 0) > (status.app ?? 0) ? 'Web' : 'App'
                  })`}
                </span>
              )}
            </span>
          </div>

          {showLastLogin && currUser?.roles?.support && (
            <div className="flex flex-col">
              <span className={cn('text-[1.3rem] font-normal', className)}>
                {`Last app login: (${formatTimestamp(
                  status.app ?? status.platform?.app
                )}) | App Version: ${recipientDoc?.version ?? 'N/A'} | OS: ${
                  recipientDoc?.app_data?.extinfo[0] === 'a2'
                    ? 'Android'
                    : 'iOS'
                }`}
              </span>
              <span className={cn('text-[1.3rem] font-normal', className)}>
                {`Last web login: (${formatTimestamp(
                  status.web ?? status.platform?.web
                )})`}
              </span>
            </div>
          )}
          {currUser?.roles?.support && recipientDocUid && (
            <div className="flex flex-col">
              <span className={cn('text-[1.3rem]', className)}>
                Name: {recipientDoc?.first} {recipientDoc?.last}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  ) : null;
});
